/* Press kit — copy buttons, downloads and the "On this page" rail.
   Loaded by every press-kit-* section, so each definition is guarded. */
(function () {
  /* Copy a value (or the text of another element) to the clipboard. */
  if (!customElements.get('pk-copy')) {
    customElements.define(
      'pk-copy',
      class extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('button');
          this.status = this.querySelector('[data-pk-copy-status]');
          if (!this.button) return;
          this.button.addEventListener('click', () => this.copy());
        }

        value() {
          if (this.dataset.value) return this.dataset.value;
          const target = this.dataset.target && document.getElementById(this.dataset.target);
          return target ? target.innerText.trim() : '';
        }

        async copy() {
          const text = this.value();
          if (!text) return;
          try {
            await navigator.clipboard.writeText(text);
          } catch (error) {
            const area = document.createElement('textarea');
            area.value = text;
            area.setAttribute('readonly', '');
            area.style.position = 'fixed';
            area.style.opacity = '0';
            document.body.appendChild(area);
            area.select();
            document.execCommand('copy');
            area.remove();
          }
          this.button.classList.add('is-copied');
          if (this.status) this.status.textContent = this.dataset.copiedLabel || '';
          clearTimeout(this.timer);
          this.timer = setTimeout(() => {
            this.button.classList.remove('is-copied');
            if (this.status) this.status.textContent = '';
          }, 2000);
        }
      }
    );
  }

  // Shopify's CDN answers a browser navigation with WebP, so a plain
  // <a download> hands out a .webp instead of the original PNG or JPG, and
  // the download attribute is ignored cross-origin anyway. fetch() accepts
  // any type, so the CDN returns the original file, and the blob keeps the
  // right filename. If anything fails, the link simply opens the file.
  // A .webp upload comes back as JPEG or PNG, so name the file after what
  // actually arrived rather than after the URL.
  const extensions = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
    'image/tiff': 'tif',
    'application/pdf': 'pdf',
    'application/zip': 'zip',
  };
  const filenameFor = (name, type) => {
    const extension = extensions[type.split(';')[0]];
    if (!extension || !name) return name;
    const base = name.replace(/\.[a-z0-9]+$/i, '');
    return `${base}.${extension}`;
  };

  if (!window.pkDownloadBound) {
    window.pkDownloadBound = true;
    document.addEventListener('click', async (event) => {
      const link = event.target.closest('a[data-pk-download]');
      if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;
      event.preventDefault();
      link.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(link.href, { mode: 'cors' });
        if (!response.ok) throw new Error(response.status);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const temp = document.createElement('a');
        temp.href = url;
        temp.download = filenameFor(link.getAttribute('download') || '', blob.type);
        document.body.appendChild(temp);
        temp.click();
        temp.remove();
        setTimeout(() => URL.revokeObjectURL(url), 4000);
      } catch (error) {
        window.open(link.href, '_blank', 'noopener');
      } finally {
        link.removeAttribute('aria-busy');
      }
    });
  }

  /* Builds its links from every [data-pk-chapter] on the page, so sections
     can be added, removed and reordered in the editor without touching it. */
  if (!customElements.get('pk-toc')) {
    customElements.define(
      'pk-toc',
      class extends HTMLElement {
        connectedCallback() {
          this.list = this.querySelector('[data-pk-toc-list]');
          this.details = this.querySelector('details');
          this.summary = this.querySelector('summary');
          if ('ResizeObserver' in window) {
            this.resizer = new ResizeObserver(() => this.syncHeight());
            this.resizer.observe(this);
          }
          this.desktop = window.matchMedia('(min-width: 1100px)');
          this.onMedia = () => this.syncOpen();
          this.desktop.addEventListener('change', this.onMedia);
          this.rebuild = () => this.build();
          // unload fires before the section leaves the DOM.
          this.rebuildLater = () => setTimeout(this.rebuild);
          document.addEventListener('shopify:section:load', this.rebuild);
          document.addEventListener('shopify:section:unload', this.rebuildLater);
          document.addEventListener('shopify:section:reorder', this.rebuild);
          // On desktop the list is a permanent rail, not a disclosure.
          this.details.addEventListener('toggle', () => {
            if (this.desktop.matches && !this.details.open) this.details.open = true;
          });
          this.list.addEventListener('click', (event) => {
            if (event.target.closest('a') && !this.desktop.matches) this.details.open = false;
          });
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', this.rebuild, { once: true });
          } else {
            this.build();
          }
        }

        disconnectedCallback() {
          this.desktop.removeEventListener('change', this.onMedia);
          document.removeEventListener('shopify:section:load', this.rebuild);
          document.removeEventListener('shopify:section:unload', this.rebuildLater);
          document.removeEventListener('shopify:section:reorder', this.rebuild);
          if (this.observer) this.observer.disconnect();
          if (this.resizer) this.resizer.disconnect();
          document.documentElement.style.removeProperty('--pk-toc-h');
        }

        // The section after the rail is pulled up by this much on desktop.
        syncHeight() {
          const height = this.hidden ? 0 : this.offsetHeight;
          document.documentElement.style.setProperty('--pk-toc-h', `${height}px`);
        }

        syncOpen() {
          if (this.desktop.matches) this.details.open = true;
          this.summary.tabIndex = this.desktop.matches ? -1 : 0;
        }

        build() {
          const chapters = Array.from(document.querySelectorAll('[data-pk-chapter][id]'));
          this.list.innerHTML = '';
          chapters.forEach((chapter) => {
            const label = chapter.dataset.pkLabel;
            if (!label) return;
            const item = document.createElement('li');
            const link = document.createElement('a');
            link.href = `#${chapter.id}`;
            link.textContent = label;
            item.appendChild(link);
            this.list.appendChild(item);
          });
          this.hidden = this.list.children.length === 0;
          this.syncOpen();
          this.syncHeight();
          this.spy(chapters);
        }

        spy(chapters) {
          if (this.observer) this.observer.disconnect();
          if (!('IntersectionObserver' in window) || chapters.length === 0) return;
          const links = new Map(
            Array.from(this.list.querySelectorAll('a')).map((link) => [link.hash.slice(1), link])
          );
          const visible = new Set();
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) visible.add(entry.target);
                else visible.delete(entry.target);
              });
              const current = chapters.find((chapter) => visible.has(chapter));
              links.forEach((link, id) => {
                if (current && id === current.id) link.setAttribute('aria-current', 'true');
                else link.removeAttribute('aria-current');
              });
            },
            { rootMargin: '-15% 0px -60% 0px' }
          );
          chapters.forEach((chapter) => this.observer.observe(chapter));
        }
      }
    );
  }
})();
